<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { Menu, ReceiptText, ShoppingCart, Loader2 } from 'lucide-vue-next';
import MenuPage from '../views/MenuPage.vue';
import CheckoutView from '../views/CheckoutView.vue';
import OrderTracker from '../components/orders/OrderTracker.vue';
import WaiterCallBell from '../components/waiter/WaiterCallBell.vue';
import StickyCart from '../components/cart/StickyCart.vue';
import { useCartStore } from '../stores/cart-store';
import { useOrdersStore } from '../stores/orders-store';
import { useSessionStore } from '../stores/session-store';

const props = defineProps<{
  currentView: 'menu' | 'details' | 'orders';
  sessionReady: boolean;
  activeOrderId: string;
  editingOrderId?: string;
}>();

const emit = defineEmits<{
  (event: 'view-change', view: 'menu' | 'details' | 'orders'): void;
  (event: 'order-success', orderId: string | number): void;
  (event: 'open-profile'): void;
  (event: 'edit-order', orderId: string): void;
  (event: 'cancel-order'): void;
}>();

const cartStore = useCartStore();
const ordersStore = useOrdersStore();
const sessionStore = useSessionStore();

const menuPageRef = ref<InstanceType<typeof MenuPage> | null>(null);

const handleEditCartItem = (item: any, index: number) => {
  if (props.currentView !== 'menu') {
    emit('view-change', 'menu');
  }
  nextTick(() => {
    menuPageRef.value?.openEditModal(item, index);
  });
};
</script>

<template>
  <section class="mobile-client-screen" data-client-screen="mobile" data-testid="client-mobile-screen">
    <a href="#main" class="skip-link">Saltar al contenido principal</a>
    <header class="mobile-client-screen__header">
      <div>
        <span>Mesa</span>
        <strong>Orden móvil</strong>
      </div>
      <button type="button" @click="cartStore.openCart">
        <ShoppingCart :size="18" />
        {{ cartStore.totalItems }}
      </button>
    </header>

    <main id="main" tabindex="-1" class="mobile-client-screen__main">
      <div v-if="sessionStore.loading && !sessionStore.bootstrapCompleted" class="mobile-client-screen__loading">
        <Loader2 class="mobile-client-screen__spinner" :size="28" />
        <p>Cargando menú...</p>
      </div>
      <MenuPage v-show="currentView === 'menu'" ref="menuPageRef" />
      <CheckoutView
        v-if="currentView === 'details'"
        :session-ready="sessionReady"
        :editing-order-id="editingOrderId"
        @back="emit('view-change', 'menu')"
        @success="emit('order-success', $event)"
        @open-profile="emit('open-profile')"
        @edit-item="handleEditCartItem"
      />
      <OrderTracker
        v-else-if="currentView === 'orders'"
        :active-order-id="activeOrderId"
        @edit-order="emit('edit-order', $event)"
        @cancel-order="emit('cancel-order')"
      />
    </main>

    <nav class="mobile-client-screen__nav" aria-label="Cliente móvil">
      <button
        type="button"
        :class="{ active: currentView === 'menu' }"
        @click="emit('view-change', 'menu')"
      >
        <Menu :size="19" />
        Menú
      </button>
      <button
        type="button"
        :class="{ active: currentView === 'details' }"
        @click="emit('view-change', 'details')"
      >
        <ShoppingCart :size="19" />
        Carrito
      </button>
      <button
        type="button"
        :class="{ active: currentView === 'orders' }"
        :disabled="!ordersStore.hasActiveOrders"
        :aria-disabled="!ordersStore.hasActiveOrders"
        :aria-label="ordersStore.hasActiveOrders ? 'Estado del pedido' : 'Estado del pedido — no disponible'"
        @click="emit('view-change', 'orders')"
      >
        <ReceiptText :size="19" />
        Estado
      </button>
    </nav>

    <StickyCart />
    <WaiterCallBell data-client-critical="waiter-call" />
  </section>
</template>

<style scoped>
.mobile-client-screen {
  min-height: 100vh;
  padding-bottom: 5.4rem;
  background:
    radial-gradient(1200px 420px at 20% -10%, rgba(15, 118, 110, 0.12), transparent 55%),
    radial-gradient(900px 360px at 100% 0%, rgba(59, 130, 246, 0.1), transparent 50%),
    #f8fafc;
}

.mobile-client-screen__header {
  position: sticky;
  top: 0;
  z-index: 180;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.95rem;
  background: rgba(255, 255, 255, 0.88);
  border-bottom: 1px solid #d6dfeb;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(16px);
}

.mobile-client-screen__header span,
.mobile-client-screen__header strong {
  display: block;
}

.mobile-client-screen__header span {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.mobile-client-screen__header strong {
  color: #172033;
  font-size: 1.04rem;
  font-weight: 860;
}

.mobile-client-screen__header button,
.mobile-client-screen__nav button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border: 0;
  cursor: pointer;
}

.mobile-client-screen__header button {
  min-width: 54px;
  /* #832: WCAG 2.5.5 minimum touch target */
  min-height: 44px;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(135deg, #ea580c, #ea580c);
  font-weight: 800;
  box-shadow: 0 8px 18px rgba(234, 88, 12, 0.32);
}

.mobile-client-screen__main {
  min-height: 100vh;
}

.mobile-client-screen__nav {
  position: fixed;
  left: 0.7rem;
  right: 0.7rem;
  bottom: 0.7rem;
  z-index: 1150;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.35rem;
  padding: 0.45rem;
  background: rgba(15, 23, 42, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.36);
  backdrop-filter: blur(10px);
}

.mobile-client-screen__nav button {
  min-height: 48px;
  border-radius: 12px;
  color: #dbe5f3;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 800;
  transition: background 0.18s ease, color 0.18s ease;
}

.mobile-client-screen__nav button.active {
  color: #172033;
  background: #ffffff;
  box-shadow: 0 6px 14px rgba(148, 163, 184, 0.3);
}

.mobile-client-screen__nav button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
